import React, { useState, createElement } from 'react';
import { User, Briefcase, MapPin, CheckCircle, ChevronRight, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
export const LoungeOnboard = () => {
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    type: '',
    fullName: '',
    email: '',
    phone: '',
    brandName: '',
    expertise: '',
    cityId: '',
    cityName: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };
  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };
  const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzR_-KmqKZFGGbKzHfTDJ1Shbo07C0RSOwx4RKTH9sU4PjA-r7elviN739j-03EfNIa/exec';
  // Submit via image beacon GET request — works in ALL environments
  // (sandboxed iframes, CSP restrictions, cross-origin, redirects)
  const submitToSheet = (data: Record<string, string>): Promise<void> => {
    return new Promise((resolve, reject) => {
      const params = new URLSearchParams(data);
      const url = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;
      // Method 1: Image beacon (most reliable, works everywhere)
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => {
        // Image onerror fires even on successful requests (since response isn't an image)
        // So we treat this as success too
        resolve();
      };
      img.src = url;
      // Safety timeout — resolve after 3s regardless
      setTimeout(() => resolve(), 3000);
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < totalSteps) {
      nextStep();
    } else {
      // Submit to Google Sheets via image beacon GET request
      setIsSubmitting(true);
      setSubmitError('');
      try {
        await submitToSheet({
          Timestamp: new Date().toISOString(),
          Type: formData.type,
          'Full Name': formData.fullName,
          Email: formData.email,
          Phone: formData.phone,
          'Brand Name': formData.brandName,
          Expertise: formData.expertise,
          'City ID': formData.cityId,
          'City Name': formData.cityName
        });
        setIsSubmitted(true);
      } catch (error) {
        console.error('Submission error:', error);
        setSubmitError('Failed to submit. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  // Custom styles for this specific page to match the reference
  const pageStyles = `
    .lounge-bg {
      background-color: #111827; /* Dark charcoal/navy */
      background-image: 
        linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
      background-size: 40px 40px;
      color: #f3f4f6;
      min-height: 100vh;
      font-family: 'Inter', sans-serif;
    }
    
    .lounge-card {
      background-color: #1f2937;
      border: 1px solid rgba(239, 68, 68, 0.2); /* Subtle red border */
      box-shadow: 0 0 20px rgba(239, 68, 68, 0.1), inset 0 0 20px rgba(239, 68, 68, 0.05); /* Red glow */
      border-radius: 12px;
    }
    
    .lounge-label {
      color: #ef4444; /* Red accent */
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      margin-bottom: 0.5rem;
      display: block;
    }
    
    .lounge-input {
      background-color: #111827;
      border: 1px solid #374151;
      color: #f3f4f6;
      border-radius: 6px;
      padding: 0.75rem 1rem;
      width: 100%;
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      transition: all 0.2s ease;
    }
    
    .lounge-input:focus {
      outline: none;
      border-color: #ef4444;
      box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.5);
    }
    
    .lounge-input::placeholder {
      color: #4b5563;
    }
    
    .lounge-btn {
      background-color: #4b5563; /* Grayed out initially */
      color: #d1d5db;
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      border: none;
      border-radius: 6px;
      padding: 1rem;
      width: 100%;
      font-weight: 600;
      transition: all 0.3s ease;
      cursor: pointer;
    }
    
    .lounge-btn:hover {
      background-color: #6b7280;
      color: #ffffff;
    }
    
    .lounge-btn.active {
      background-color: #ef4444;
      color: #ffffff;
    }
    
    .lounge-btn.active:hover {
      background-color: #dc2626;
      box-shadow: 0 0 15px rgba(239, 68, 68, 0.4);
    }
    
    .step-indicator {
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      color: #ef4444;
      font-size: 0.875rem;
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    
    .progress-bar-container {
      height: 4px;
      background-color: #374151;
      border-radius: 2px;
      width: 60px;
      overflow: hidden;
    }
    
    .progress-bar-fill {
      height: 100%;
      background-color: #ef4444;
      box-shadow: 0 0 8px #ef4444;
      transition: width 0.3s ease;
    }
    
    .scanline {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: rgba(239, 68, 68, 0.3);
      box-shadow: 0 0 10px rgba(239, 68, 68, 0.5);
      animation: scan 8s linear infinite;
      pointer-events: none;
      z-index: 50;
    }
    
    @keyframes scan {
      0% { top: -10%; }
      100% { top: 110%; }
    }
  `;
  // If submitted successfully, show success screen
  if (isSubmitted) {
    return <div className="lounge-bg flex flex-col items-center justify-center py-12 px-4 relative" data-id="element-3635">
        <style data-id="element-3636">{`{pageStyles}`}</style>
        <div className="scanline" data-id="element-3637"></div>
        <div className="w-full max-w-xl" data-id="element-3638">
          <div className="lounge-card p-8 md:p-10 text-center" data-id="element-3639">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center" data-id="element-3640">
              <CheckCircle className="text-green-500" size={40} data-id="element-3641" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-3642">
              SEQUENCE INITIALIZED
            </h2>
            <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-3643">
              Your telemetry data has been transmitted successfully.
              <br data-id="element-3644" />
              Welcome to The Lounge.
            </p>
            <div className="space-y-4" data-id="element-3645">
              <a href="https://thepod.thenetworkingsalon.live/salon" className="lounge-btn active inline-flex justify-center items-center gap-2 w-full" data-id="element-3646">
                PROCEED TO PAYMENT
                <ChevronRight size={18} data-id="element-3647" />
              </a>
              <a href="https://chat.whatsapp.com/LAWDlIeeKSuFIUq1dwvZYe" target="_blank" rel="noopener noreferrer" className="lounge-btn inline-flex justify-center items-center gap-2 w-full" style={{
              backgroundColor: '#25D366',
              color: '#ffffff'
            }} data-id="element-3648">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-3649">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-3650" />
                </svg>
                JOIN WHATSAPP FREE LOUNGE
              </a>
              <Link to="/" className="lounge-btn inline-flex justify-center items-center gap-2 w-full" data-id="element-3651">
                <ArrowLeft size={18} data-id="element-3652" />
                RETURN TO BASE
              </Link>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="lounge-bg flex flex-col items-center justify-center py-12 px-4 relative" data-id="element-3653">
      <style data-id="element-3654">{`{pageStyles}`}</style>
      <div className="scanline" data-id="element-3655"></div>

      {/* Top Navigation Bar (simulated) */}
      <div className="absolute top-0 left-0 w-full p-6 flex justify-between items-center z-10" data-id="element-3656">
        <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-id="element-3657">
          <ArrowLeft size={20} data-id="element-3658" />
          <span className="font-mono text-sm uppercase tracking-wider" data-id="element-3659">
            Return to Base
          </span>
        </Link>
        <div className="flex gap-6 items-center" data-id="element-3660">
          <span className="text-gray-300 font-bold text-sm tracking-wider" data-id="element-3661">
            ABOUT THE SALON
          </span>
          <a href="https://thepod.thenetworkingsalon.live/salon" className="bg-red-500 text-white px-4 py-1.5 rounded-full text-sm font-bold tracking-wider hover:bg-red-600 transition-colors" data-id="element-3662">
            PAYMENT
          </a>
        </div>
      </div>

      <div className="w-full max-w-xl mt-12" data-id="element-3663">
        <div className="lounge-card p-8 md:p-10 relative overflow-hidden" data-id="element-3664">
          {/* Header / Step Indicator */}
          <div className="flex justify-between items-center mb-10 border-b border-gray-700 pb-6" data-id="element-3665">
            <div className="step-indicator" data-id="element-3666">
              <span className="border border-red-500/30 p-1 rounded" data-id="element-3667">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3668">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-3669"></path>
                  <polyline points="14 2 14 8 20 8" data-id="element-3670"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13" data-id="element-3671"></line>
                  <line x1="16" y1="17" x2="8" y2="17" data-id="element-3672"></line>
                  <polyline points="10 9 9 9 8 9" data-id="element-3673"></polyline>
                </svg>
              </span>
              <span data-id="element-3674">
                STEP 0{step} / 0{totalSteps}
              </span>
            </div>
            <div className="progress-bar-container" data-id="element-3675">
              <div className="progress-bar-fill" style={{
              width: `${step / totalSteps * 100}%`
            }} data-id="element-3676"></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-id="element-3677">
            {/* Step 1: Identification */}
            {step === 1 && <div className="animate-fade-in" data-id="element-3678">
                <div className="flex items-center gap-3 mb-2" data-id="element-3679">
                  <User className="text-red-500" size={28} data-id="element-3680" />
                  <h2 className="text-3xl font-bold text-white" data-id="element-3681">
                    Identification
                  </h2>
                </div>
                <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-3682">
                  Enter your primary telemetry data.
                </p>

                <div className="space-y-6" data-id="element-3683">
                  <div data-id="element-3684">
                    <label className="lounge-label" data-id="element-3685">FULL_NAME</label>
                    <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className="lounge-input" placeholder="John Doe" required data-id="element-3686" />
                  </div>
                  <div data-id="element-3687">
                    <label className="lounge-label" data-id="element-3688">EMAIL_ADDRESS</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="lounge-input" placeholder="john@example.com" required data-id="element-3689" />
                  </div>
                  <div data-id="element-3690">
                    <label className="lounge-label" data-id="element-3691">COMM_LINK (PHONE)</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="lounge-input" placeholder="+234 800 000 0000" required data-id="element-3692" />
                  </div>
                </div>
              </div>}

            {/* Step 2: Professional Profile */}
            {step === 2 && <div className="animate-fade-in" data-id="element-3693">
                <div className="flex items-center gap-3 mb-2" data-id="element-3694">
                  <Briefcase className="text-red-500" size={28} data-id="element-3695" />
                  <h2 className="text-3xl font-bold text-white" data-id="element-3696">
                    Professional Specs
                  </h2>
                </div>
                <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-3697">
                  Define your operational parameters.
                </p>

                <div className="space-y-6" data-id="element-3698">
                  <div data-id="element-3699">
                    <label className="lounge-label" data-id="element-3700">TYPE</label>
                    <select name="type" value={formData.type} onChange={handleChange} className="lounge-input appearance-none" required data-id="element-3701">
                      <option value="" disabled data-id="element-3702">
                        Select Type...
                      </option>
                      <option value="Individual" data-id="element-3703">Individual</option>
                      <option value="Business" data-id="element-3704">Business</option>
                      <option value="Organization" data-id="element-3705">Organization</option>
                      <option value="Student" data-id="element-3706">Student</option>
                    </select>
                  </div>
                  <div data-id="element-3707">
                    <label className="lounge-label" data-id="element-3708">BRAND_NAME</label>
                    <input type="text" name="brandName" value={formData.brandName} onChange={handleChange} className="lounge-input" placeholder="Your brand or company name" required data-id="element-3709" />
                  </div>
                  <div data-id="element-3710">
                    <label className="lounge-label" data-id="element-3711">EXPERTISE</label>
                    <input type="text" name="expertise" value={formData.expertise} onChange={handleChange} className="lounge-input" placeholder="e.g. Marketing, Engineering, Design" required data-id="element-3712" />
                  </div>
                </div>
              </div>}

            {/* Step 3: Location */}
            {step === 3 && <div className="animate-fade-in" data-id="element-3713">
                <div className="flex items-center gap-3 mb-2" data-id="element-3714">
                  <MapPin className="text-red-500" size={28} data-id="element-3715" />
                  <h2 className="text-3xl font-bold text-white" data-id="element-3716">
                    Location Data
                  </h2>
                </div>
                <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-3717">
                  Provide your geographic coordinates.
                </p>

                <div className="space-y-6" data-id="element-3718">
                  <div data-id="element-3719">
                    <label className="lounge-label" data-id="element-3720">CITY_ID</label>
                    <input type="text" name="cityId" value={formData.cityId} onChange={handleChange} className="lounge-input" placeholder="e.g. LOS, ABJ, PHC" required data-id="element-3721" />
                  </div>
                  <div data-id="element-3722">
                    <label className="lounge-label" data-id="element-3723">CITY_NAME</label>
                    <input type="text" name="cityName" value={formData.cityName} onChange={handleChange} className="lounge-input" placeholder="e.g. Lagos, Abuja, Port Harcourt" required data-id="element-3724" />
                  </div>
                </div>
              </div>}

            {/* Step 4: Confirmation */}
            {step === 4 && <div className="animate-fade-in" data-id="element-3725">
                <div className="flex items-center gap-3 mb-2" data-id="element-3726">
                  <CheckCircle className="text-red-500" size={28} data-id="element-3727" />
                  <h2 className="text-3xl font-bold text-white" data-id="element-3728">
                    Verification
                  </h2>
                </div>
                <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-3729">
                  Review your telemetry data before transmission.
                </p>

                <div className="space-y-4 bg-gray-900/50 p-6 rounded-lg border border-gray-800" data-id="element-3730">
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3731">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3732">NAME:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3733">
                      {formData.fullName || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3734">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3735">EMAIL:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3736">
                      {formData.email || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3737">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3738">PHONE:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3739">
                      {formData.phone || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3740">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3741">TYPE:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3742">
                      {formData.type || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3743">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3744">BRAND:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3745">
                      {formData.brandName || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3746">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3747">
                      EXPERTISE:
                    </span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3748">
                      {formData.expertise || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3749">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3750">CITY:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3751">
                      {formData.cityName || 'N/A'} ({formData.cityId || 'N/A'})
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-1" data-id="element-3752">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3753">
                      STATUS:
                    </span>
                    <span className="text-green-500 font-mono text-sm col-span-2 flex items-center gap-2" data-id="element-3754">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-3755"></span>
                      READY FOR UPLOAD
                    </span>
                  </div>
                </div>
              </div>}

            {/* Navigation Buttons */}
            <div className="mt-10 pt-6 border-t border-gray-800 flex gap-4" data-id="element-3756">
              {step > 1 && <button type="button" onClick={prevStep} className="px-6 py-3 rounded border border-gray-700 text-gray-400 font-mono text-sm hover:bg-gray-800 hover:text-white transition-colors" disabled={isSubmitting} data-id="element-3757">
                  &lt; BACK
                </button>}
              <button type="submit" disabled={isSubmitting} className={`lounge-btn flex-1 flex justify-center items-center gap-2 ${step === 1 && formData.fullName && formData.email && formData.phone || step === 2 && formData.type && formData.brandName && formData.expertise || step === 3 && formData.cityId && formData.cityName || step === 4 ? 'active' : ''} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-3758">
                {isSubmitting ? <>
                    <Loader2 size={18} className="animate-spin" data-id="element-3759" />
                    TRANSMITTING...
                  </> : step === totalSteps ? 'INITIALIZE SEQUENCE' : <>
                    CONTINUE
                    <ChevronRight size={18} data-id="element-3760" />
                  </>}
              </button>
            </div>
            {submitError && <p className="mt-4 text-red-500 font-mono text-sm text-center" data-id="element-3761">
                {submitError}
              </p>}
          </form>
        </div>
      </div>
    </div>;
};